import { Navigate, Route, Routes } from 'react-router-dom';
import { MODULES } from './agency/modules';
import { RedirectIfSignedIn, RequireAuth } from './auth/RequireAuth';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { reputationAgencyRoutes, reputationPublicRoutes } from './reputation/routes';
import { communitiesAgencyRoutes, coursesAgencyRoutes, portalPublicRoutes } from './courses/routes';
import { phoneAgencyRoutes } from './phone/routes';
import { voiceAiAgencyRoutes } from './voice-ai/routes';
import { integrationsAgencyRoutes, integrationsSignedInRoutes } from './integrations/routes';
import { knowledgeAgencyRoutes } from './knowledge/routes';
import { aiEmployeesAgencyRoutes } from './ai-employees/routes';
import { medspaAgencyRoutes, medspaPublicRoutes } from './medspa/routes';
import { homeServicesAgencyRoutes, homeServicesPublicRoutes } from './home-services/routes';
import { dentalAgencyRoutes, dentalPublicRoutes } from './dental/routes';
import { lazyRoute } from './lazyRoute';

// Screens load on first visit (route-level code splitting, src/lazyRoute.tsx).
// The signed-in shells share one chunk (src/shell/routes.ts).
// Sign-in is in the first bundle; the other signed-out pages load when opened (plan.md, B6)
const RegisterPage = lazyRoute(() => import('./pages/RegisterPage').then((m) => m.RegisterPage));
const ForgotPasswordPage = lazyRoute(() => import('./pages/ForgotPasswordPage').then((m) => m.ForgotPasswordPage));
const ResetPasswordPage = lazyRoute(() => import('./pages/ResetPasswordPage').then((m) => m.ResetPasswordPage));
const InviteAcceptPage = lazyRoute(() => import('./pages/InviteAcceptPage').then((m) => m.InviteAcceptPage));
// Module areas: each area's route table is its own chunk (plan.md, B4)
const PaymentsRoutes = lazyRoute(() => import('./payments/routes').then((m) => m.PaymentsRoutes));
const CampaignsRoutes = lazyRoute(() => import('./campaigns/routes').then((m) => m.CampaignsRoutes));
const AutomationsRoutes = lazyRoute(() => import('./automations/routes').then((m) => m.AutomationsRoutes));
const SitesRoutes = lazyRoute(() => import('./sites/routes').then((m) => m.SitesRoutes));
const SocialRoutes = lazyRoute(() => import('./social/routes').then((m) => m.SocialRoutes));
const SnapshotsRoutes = lazyRoute(() => import('./snapshots/routes').then((m) => m.SnapshotsRoutes));
const ProposalsRoutes = lazyRoute(() => import('./proposals/routes').then((m) => m.ProposalsRoutes));
const MigrateRoutes = lazyRoute(() => import('./migrate/routes').then((m) => m.MigrateRoutes));
const MobileAppRoutes = lazyRoute(() => import('./mobile-app/MobileAppRoutes').then((m) => m.MobileAppRoutes));
// Public pages the modules link to (no sign-in)
const PayPage = lazyRoute(() => import('./payments/PayPage').then((m) => m.PayPage));
const UnsubscribePage = lazyRoute(() => import('./campaigns/UnsubscribePage').then((m) => m.UnsubscribePage));
const SigningPage = lazyRoute(() => import('./proposals/SigningPage').then((m) => m.SigningPage));
const VerifyPage = lazyRoute(() => import('./proposals/VerifyPage').then((m) => m.VerifyPage));
const PublicApprovalPage = lazyRoute(() => import('./social/PublicApprovalPage').then((m) => m.PublicApprovalPage));
const AgencyLayout = lazyRoute(() => import('./shell/routes').then((m) => m.AgencyLayout));
const AccountShell = lazyRoute(() => import('./shell/routes').then((m) => m.AccountShell));
const AgencyBillingPage = lazyRoute(() => import('./billing/AgencyBillingPage').then((m) => m.AgencyBillingPage));
const SubaccountBillingPage = lazyRoute(() => import('./billing/SubaccountBillingPage').then((m) => m.SubaccountBillingPage));
const AiSettingsPage = lazyRoute(() => import('./ai/AiSettingsPage').then((m) => m.AiSettingsPage));
const InboxPage = lazyRoute(() => import('./inbox/InboxPage').then((m) => m.InboxPage));
const InboxSettingsPage = lazyRoute(() => import('./inbox/InboxSettingsPage').then((m) => m.InboxSettingsPage));
const AgenciesPage = lazyRoute(() => import('./pages/AgenciesPage').then((m) => m.AgenciesPage));
const AgencyDashboard = lazyRoute(() => import('./pages/AgencyDashboard').then((m) => m.AgencyDashboard));
const ContactDetailPage = lazyRoute(() => import('./pages/contacts/ContactDetailPage').then((m) => m.ContactDetailPage));
const ContactSettingsPage = lazyRoute(() => import('./pages/contacts/ContactSettingsPage').then((m) => m.ContactSettingsPage));
const ContactsPage = lazyRoute(() => import('./pages/contacts/ContactsPage').then((m) => m.ContactsPage));
const ImportPage = lazyRoute(() => import('./pages/contacts/ImportPage').then((m) => m.ImportPage));
const ManageBookingPage = lazyRoute(() => import('./pages/booking/BookingPages').then((m) => m.ManageBookingPage));
const PublicBookingPage = lazyRoute(() => import('./pages/booking/BookingPages').then((m) => m.PublicBookingPage));
const PublicCalendarBookingPage = lazyRoute(() => import('./pages/booking/BookingPages').then((m) => m.PublicCalendarBookingPage));
const AppointmentsView = lazyRoute(() => import('./pages/calendar/CalendarSection').then((m) => m.AppointmentsView));
const CalendarSection = lazyRoute(() => import('./pages/calendar/CalendarSection').then((m) => m.CalendarSection));
const CalendarSettingsView = lazyRoute(() => import('./pages/calendar/CalendarSettingsView').then((m) => m.CalendarSettingsView));
const CalendarsView = lazyRoute(() => import('./pages/calendar/CalendarsView').then((m) => m.CalendarsView));
const AgencySettingsPage = lazyRoute(() => import('./pages/AgencySettingsPage').then((m) => m.AgencySettingsPage));
const MembersPage = lazyRoute(() => import('./pages/MembersPage').then((m) => m.MembersPage));
const OpportunityPage = lazyRoute(() => import('./pages/pipelines/OpportunityPage').then((m) => m.OpportunityPage));
const PipelineBoardPage = lazyRoute(() => import('./pages/pipelines/PipelineBoardPage').then((m) => m.PipelineBoardPage));
const PipelineReportsPage = lazyRoute(() => import('./pages/pipelines/PipelineReportsPage').then((m) => m.PipelineReportsPage));
const PipelinesLayout = lazyRoute(() => import('./pages/pipelines/PipelinesLayout').then((m) => m.PipelinesLayout));
const PipelineSettingsPage = lazyRoute(() => import('./pages/pipelines/PipelineSettingsPage').then((m) => m.PipelineSettingsPage));
const SecurityPage = lazyRoute(() => import('./pages/SecurityPage').then((m) => m.SecurityPage));
const SubaccountProfilePage = lazyRoute(() => import('./pages/SubaccountProfilePage').then((m) => m.SubaccountProfilePage));
const SubaccountsPage = lazyRoute(() => import('./pages/SubaccountsPage').then((m) => m.SubaccountsPage));
const TeamDirectoryPage = lazyRoute(() => import('./pages/TeamDirectoryPage').then((m) => m.TeamDirectoryPage));

/** Modules with real screens (the rest show a "coming soon" placeholder). */
const BUILT_MODULES: ReadonlySet<string> = new Set(['ai', 'automations', 'calendar', 'campaigns', 'communities', 'contacts', 'courses', 'inbox', 'integrations', 'knowledge', 'ai_employees', 'medspa', 'home_services', 'dental', 'payments', 'phone', 'pipelines', 'proposals', 'reputation', 'sites', 'social', 'voice_ai']);

/** React Router v7 behaviours, opted into now (silences the v6 deprecation warnings). */
export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true } as const;

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginPage />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfSignedIn>
            <RegisterPage />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <RedirectIfSignedIn>
            <ForgotPasswordPage />
          </RedirectIfSignedIn>
        }
      />
      {/* Reachable signed in or out: the token in the link is the credential. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite" element={<InviteAcceptPage />} />
      <Route element={<RequireAuth />}>
        <Route path="/" element={<Navigate to="/agencies" replace />} />
        {integrationsSignedInRoutes}
        <Route element={<AccountShell />}>
          <Route path="/agencies" element={<AgenciesPage />} />
          <Route path="/security" element={<SecurityPage />} />
        </Route>
        <Route path="/agencies/:agencyId" element={<AgencyLayout />}>
          <Route index element={<AgencyDashboard />} />
          <Route path="subaccounts" element={<SubaccountsPage />} />
          <Route path="subaccounts/:subaccountId/profile" element={<SubaccountProfilePage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="pipelines" element={<PipelinesLayout />}>
            <Route index element={<PipelineBoardPage />} />
            <Route path="reports" element={<PipelineReportsPage />} />
            <Route path="settings" element={<PipelineSettingsPage />} />
            <Route path="opportunities/:opportunityId" element={<OpportunityPage />} />
          </Route>
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="contacts/import" element={<ImportPage />} />
          <Route path="contacts/settings" element={<ContactSettingsPage />} />
          <Route path="contacts/:contactId" element={<ContactDetailPage />} />
          <Route path="payments/*" element={<PaymentsRoutes />} />
          <Route path="campaigns/*" element={<CampaignsRoutes />} />
          <Route path="automations/*" element={<AutomationsRoutes />} />
          <Route path="sites/*" element={<SitesRoutes />} />
          <Route path="social/*" element={<SocialRoutes />} />
          {reputationAgencyRoutes}
          <Route path="snapshots/*" element={<SnapshotsRoutes />} />
          <Route path="migrate/*" element={<MigrateRoutes />} />
          {coursesAgencyRoutes}
          {communitiesAgencyRoutes}
          {phoneAgencyRoutes}
          {voiceAiAgencyRoutes}
          <Route path="proposals/*" element={<ProposalsRoutes />} />
          <Route path="mobile-app/*" element={<MobileAppRoutes />} />
          {integrationsAgencyRoutes}
          {knowledgeAgencyRoutes}
          {aiEmployeesAgencyRoutes}
          {medspaAgencyRoutes}
          {homeServicesAgencyRoutes}
          {dentalAgencyRoutes}
          <Route path="team" element={<TeamDirectoryPage />} />
          <Route path="settings" element={<AgencySettingsPage />} />
          <Route path="billing" element={<AgencyBillingPage />} />
          <Route path="account-billing" element={<SubaccountBillingPage />} />
          <Route path="calendar" element={<CalendarSection />}>
            <Route index element={<AppointmentsView />} />
            <Route path="calendars" element={<CalendarsView />} />
            <Route path="calendars/:calendarId" element={<CalendarSettingsView />} />
          </Route>
          <Route path="inbox" element={<InboxPage />} />
          <Route path="inbox/settings" element={<InboxSettingsPage />} />
          <Route path="inbox/:conversationId" element={<InboxPage />} />
          <Route path="ai" element={<AiSettingsPage />} />
          {MODULES.filter((m) => !BUILT_MODULES.has(m.path)).map((m) => (
            <Route key={m.path} path={m.path} element={<ComingSoonPage module={m} />} />
          ))}
        </Route>
      </Route>
      {/* Hosted pay page (invoices, links, texts), unsubscribe, signing, social approval: no sign-in */}
      <Route path="/pay/:token" element={<PayPage />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      {portalPublicRoutes}
      <Route path="/sign" element={<SigningPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/social-approval" element={<PublicApprovalPage />} />
      {reputationPublicRoutes}
      {medspaPublicRoutes}
      {homeServicesPublicRoutes}
      {dentalPublicRoutes}
      {/* Public booking: no sign-in */}
      <Route path="/book/manage/:token" element={<ManageBookingPage />} />
      <Route path="/book/:pageSlug" element={<PublicBookingPage />} />
      <Route path="/book/:pageSlug/:calendarSlug" element={<PublicCalendarBookingPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
